import { Profile } from './profile.model';
import { IUser, UserModel } from './user.model';

/**
 * Fila cruda de Firebase Auth que acompaña a CADA elemento de `GET /users`,
 * este exista o no en MySQL. Sirve para nombrar/fechar a los usuarios que
 * todavía viven solo en Firebase (la versión anterior de KeyStoreMan no los
 * registraba en base de datos).
 */
export interface IUserListItemFirebase {
  displayName: string | null;
  createdAt: string | null;
  lastSignInAt: string | null;
}

/**
 * Elemento de `GET /users` (ver `UserService.getUsers`). A diferencia de
 * `IUser`, representa la UNION de Firebase Auth + MySQL: todo lo que viene de
 * la base de datos llega en `null` cuando el usuario aun no esta registrado
 * ahi (`registered === false`). `firebase` en cambio SIEMPRE viene.
 *
 * OJO: esta interfaz es del LISTADO. La ficha de detalle (`/users/{uid}`)
 * sigue usando `IUser`/`UserModel` sin tocar, porque solo existe para
 * usuarios que SI estan en la base de datos.
 */
export interface IUserListItem {
  uid: string;
  email: string | null;
  registered: boolean;
  names: string | null;
  lastNames: string | null;
  profile: Profile | null;
  status: boolean | null;
  lastLoginAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  /**
   * Aceptación de los Términos y condiciones y el Aviso de privacidad.
   * Columnas de `USERS` (`keystoreman-app-api/.../user_schema.py`): NOT NULL
   * con `default=False` para quien SI esta registrado, por eso aqui viajan en
   * `null` solo cuando `registered === false` (igual que `profile`/`status`) —
   * un usuario que solo vive en Firebase Auth nunca paso por ese flujo. Las
   * fechas de sello (`...At`) SI pueden ser `null` aun estando registrado: el
   * booleano puede seguir en `false` si todavia no acepta.
   */
  acceptedTerms: boolean | null;
  acceptedTermsAt: string | null;
  acceptedPrivacy: boolean | null;
  acceptedPrivacyAt: string | null;
  firebase: IUserListItemFirebase;
}

export class UserListItemFirebaseModel {
  displayName: string | null;
  createdAt: Date | null;
  lastSignInAt: Date | null;

  constructor(data: IUserListItemFirebase) {
    this.displayName = data.displayName ?? null;
    this.createdAt = data.createdAt ? new Date(data.createdAt) : null;
    this.lastSignInAt = data.lastSignInAt ? new Date(data.lastSignInAt) : null;
  }
}

export class UserListItemModel {
  uid: string;
  email: string | null;
  registered: boolean;
  names: string | null;
  lastNames: string | null;
  profile: Profile | null;
  status: boolean | null;
  lastLoginAt: Date | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  acceptedTerms: boolean | null;
  acceptedTermsAt: Date | null;
  acceptedPrivacy: boolean | null;
  acceptedPrivacyAt: Date | null;
  firebase: UserListItemFirebaseModel;

  constructor(data: IUserListItem) {
    this.uid = data.uid;
    this.email = data.email ?? null;
    this.registered = data.registered;
    this.names = data.names ?? null;
    this.lastNames = data.lastNames ?? null;
    this.profile = data.profile ?? null;
    this.status = data.status ?? null;
    this.lastLoginAt = data.lastLoginAt ? new Date(data.lastLoginAt) : null;
    this.createdAt = data.createdAt ? new Date(data.createdAt) : null;
    this.updatedAt = data.updatedAt ? new Date(data.updatedAt) : null;
    this.acceptedTerms = data.acceptedTerms ?? null;
    this.acceptedTermsAt = data.acceptedTermsAt ? new Date(data.acceptedTermsAt) : null;
    this.acceptedPrivacy = data.acceptedPrivacy ?? null;
    this.acceptedPrivacyAt = data.acceptedPrivacyAt ? new Date(data.acceptedPrivacyAt) : null;
    this.firebase = new UserListItemFirebaseModel(data.firebase);
  }

  /**
   * Registrado: nombre + apellidos de la base de datos. Sin registrar: el
   * `displayName` que trae Firebase Auth (puede no existir). Con todo vacio,
   * cae al correo y, en el peor caso, al uid: nunca deja el nombre en blanco.
   */
  get fullName(): string {
    if (this.registered) {
      const composed = `${this.names ?? ''} ${this.lastNames ?? ''}`.trim();
      return composed || this.email || this.uid;
    }
    return this.firebase.displayName || this.email || this.uid;
  }

  get isSudo(): boolean {
    return this.profile === 'sudo';
  }

  /**
   * Resumen del estatus legal, para pintar una sola etiqueta escaneable en
   * vez de dos columnas booleanas. `null` = no aplica (usuario sin registrar,
   * nunca pasó por el flujo de aceptación).
   */
  get legalAcceptanceStatus(): 'both' | 'missingTerms' | 'missingPrivacy' | 'missingBoth' | null {
    if (!this.registered) return null;
    const terms = this.acceptedTerms === true;
    const privacy = this.acceptedPrivacy === true;
    if (terms && privacy) return 'both';
    if (!terms && !privacy) return 'missingBoth';
    return terms ? 'missingPrivacy' : 'missingTerms';
  }

  /**
   * Reconstruye el `UserModel` que exige el modal de edicion (sus campos NO
   * son anulables). Solo tiene sentido para un usuario YA registrado: para
   * uno que no lo esta, no hay nada que editar y devuelve `null`. `uid` de
   * quien creo/actualizo no viaja en el listado; van en `null` porque el
   * modal no los usa (son de solo lectura en la ficha de detalle, que hace su
   * propio `GET /users/{uid}` con el dato real).
   */
  toUserModel(): UserModel | null {
    if (!this.registered) return null;

    const iso = (date: Date | null): string => (date ?? new Date()).toISOString();
    const raw: IUser = {
      uid: this.uid,
      names: this.names ?? '',
      lastNames: this.lastNames ?? '',
      email: this.email ?? '',
      profile: this.profile ?? 'user',
      lastLoginAt: this.lastLoginAt ? this.lastLoginAt.toISOString() : null,
      status: this.status ?? true,
      uidCreatedBy: null,
      uidUpdatedBy: null,
      createdAt: iso(this.createdAt),
      updatedAt: iso(this.updatedAt),
    };
    return new UserModel(raw);
  }
}
